import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { useIsMobile } from "../../hooks/useResponsive";
import { db, storage } from "../../firebase";
import { useToast } from "../../components/Toast";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  onSnapshot,
  Timestamp,
  doc,
  updateDoc,
  increment,
  limit,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  LuSend,
  LuMic,
  LuLoader,
  LuTriangleAlert,
  LuPlay,
  LuPause,
  LuSquare,
  LuMessageCircle,
} from "react-icons/lu";

// Pre-generated audio wave heights (avoids Math.random on every render)
const WAVE_HEIGHTS = [8, 14, 6, 18, 10, 16, 7, 20, 12, 15, 9, 17];

const AudioBubble = React.memo(({ msg, mine, playingAudioId, onPlay, formatDuration }) => (
  <div>
    <div style={styles.audioRow}>
      <button
        style={{
          ...styles.audioPlayBtn,
          color: mine ? "#ffffff" : "#0080ff",
        }}
        onClick={() => onPlay(msg.audioUrl, msg.id)}
      >
        {playingAudioId === msg.id ? <LuPause size={14} /> : <LuPlay size={14} />}
      </button>
      <div style={styles.audioWave}>
        {WAVE_HEIGHTS.map((h, i) => (
          <div
            key={i}
            style={{
              width: 3,
              height: h,
              borderRadius: 2,
              backgroundColor: mine ? "rgba(255,255,255,0.5)" : "#0080ff50",
            }}
          />
        ))}
      </div>
      <span style={{ fontSize: 11, opacity: 0.7 }}>
        {formatDuration(msg.audioDuration || 0)}
      </span>
    </div>
    {msg.audioTranscription && (
      <p style={{
        fontSize: 12,
        opacity: 0.7,
        margin: "6px 0 0",
        fontStyle: "italic",
      }}>
        {msg.audioTranscription}
      </p>
    )}
  </div>
));

const MessageBubble = React.memo(({ msg, mine, playingAudioId, onPlay, formatDuration, formatTimestamp }) => (
  <div
    style={{
      ...styles.messageBubbleRow,
      justifyContent: mine ? "flex-end" : "flex-start",
    }}
  >
    <div
      style={{
        ...styles.messageBubble,
        backgroundColor: mine ? "#0080ff" : "#f3f4f6",
        color: mine ? "#ffffff" : "#111827",
        borderBottomRightRadius: mine ? 4 : 16,
        borderBottomLeftRadius: mine ? 16 : 4,
      }}
    >
      {!mine && (
        <span style={styles.senderName}>{msg.senderName}</span>
      )}
      {msg.type === "audio" ? (
        <AudioBubble
          msg={msg}
          mine={mine}
          playingAudioId={playingAudioId}
          onPlay={onPlay}
          formatDuration={formatDuration}
        />
      ) : (
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
          {msg.message}
        </p>
      )}
      <span style={{
        ...styles.messageTime,
        color: mine ? "rgba(255,255,255,0.7)" : "#6b7280",
      }}>
        {formatTimestamp(msg.timestamp)}
      </span>
    </div>
  </div>
));

// Isolated input bar — typing here does NOT re-render the message list
const ChatInputBar = React.memo(({ chatId, userid, userName, onStartRecording, onOptimisticMessage }) => {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || !chatId || sending) return;
    setText("");
    setSending(true);

    // Show message immediately (optimistic update)
    const now = Timestamp.now();
    const optimisticMsg = {
      id: `optimistic_${Date.now()}`,
      chatId,
      senderId: String(userid),
      senderName: userName || "Empleado",
      message: trimmed,
      timestamp: now,
      read: false,
      type: "text",
    };
    onOptimisticMessage?.(optimisticMsg);

    try {
      await addDoc(collection(db, "employee_chat_messages"), {
        chatId,
        senderId: String(userid),
        senderName: userName || "Empleado",
        message: trimmed,
        timestamp: now,
        read: false,
        type: "text",
      });

      await updateDoc(doc(db, "employee_chats", chatId), {
        lastMessage: trimmed,
        lastMessageAt: Timestamp.now(),
        unreadCountAdmin: increment(1),
      });
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [text, chatId, sending, userid, userName, onOptimisticMessage]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <div style={styles.inputBar}>
      <button
        style={styles.audioBtn}
        onMouseDown={onStartRecording}
        title="Mantener presionado para grabar"
      >
        <LuMic size={16} />
      </button>
      <input
        ref={inputRef}
        type="text"
        style={styles.textInput}
        placeholder="Escribí un mensaje..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={sending}
      />
      <button
        style={{
          ...styles.sendBtn,
          opacity: text.trim() ? 1 : 0.5,
        }}
        onClick={handleSend}
        disabled={!text.trim() || sending}
      >
        {sending ? (
          <LuLoader size={16} style={{ animation: "spin 1s linear infinite" }} />
        ) : (
          <LuSend size={16} />
        )}
      </button>
    </div>
  );
});

// Memoized message list — only re-renders when messages or playingAudioId change
const MessageList = React.memo(({ messages, userIdStr, playingAudioId, playAudio, formatDuration, formatTimestamp, messagesEndRef }) => (
  <>
    {messages.map((msg) => (
      <MessageBubble
        key={msg.id}
        msg={msg}
        mine={String(msg.senderId) === userIdStr}
        playingAudioId={playingAudioId}
        onPlay={playAudio}
        formatDuration={formatDuration}
        formatTimestamp={formatTimestamp}
      />
    ))}
    <div ref={messagesEndRef} />
  </>
));

const ChatEmployee = ({ userid, userName }) => {
  const isMobile = useIsMobile();
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatId, setChatId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [playingAudioId, setPlayingAudioId] = useState(null);

  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const chatIdRef = useRef(null); // keep chatId accessible in closures

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (selectedBuilding && userid) {
      initChat();
    } else {
      setLoading(false);
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [selectedBuilding, userid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll on new messages
  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    if (messages.length > 0) {
      const isInitial = prevMsgCountRef.current === 0;
      messagesEndRef.current?.scrollIntoView({
        behavior: isInitial ? "instant" : "smooth",
      });
      prevMsgCountRef.current = messages.length;
    }
  }, [messages.length]);

  const initChat = async () => {
    setLoading(true);
    try {
      const buildingId = Number(selectedBuilding.buildingid);
      const empId = String(userid);

      const chatQ = query(
        collection(db, "employee_chats"),
        where("buildingid", "==", buildingId),
        where("employeeid", "==", empId),
        limit(1)
      );
      const chatSnap = await getDocs(chatQ);

      let existingChatId = null;
      if (!chatSnap.empty) {
        existingChatId = chatSnap.docs[0].id;
      } else {
        const chatRef = await addDoc(collection(db, "employee_chats"), {
          buildingid: buildingId,
          employeeid: empId,
          employeeName: userName || "Empleado",
          createdAt: Timestamp.now(),
          lastMessage: "",
          lastMessageAt: Timestamp.now(),
        });
        existingChatId = chatRef.id;
      }

      setChatId(existingChatId);
      chatIdRef.current = existingChatId;

      // Reset unread counter for employee when opening chat
      try {
        await updateDoc(doc(db, "employee_chats", existingChatId), {
          unreadCountEmployee: 0,
        });
      } catch (e) {
        // Field may not exist yet, ignore
      }

      const messagesQ = query(
        collection(db, "employee_chat_messages"),
        where("chatId", "==", existingChatId)
      );

      unsubscribeRef.current = onSnapshot(
        messagesQ,
        (snapshot) => {
          const msgs = [];
          snapshot.forEach((d) => {
            msgs.push({ id: d.id, ...d.data() });
          });
          // Orden cronológico en cliente (evita índice compuesto chatId+timestamp).
          msgs.sort((a, b) => (a.timestamp?.toMillis?.() || 0) - (b.timestamp?.toMillis?.() || 0));
          setMessages(msgs);
          setLoading(false);
        },
        (error) => {
          console.error("onSnapshot error (may need composite index):", error);
          // If index missing, fallback to polling: load messages once
          const fallbackLoad = async () => {
            try {
              const fallbackQ = query(
                collection(db, "employee_chat_messages"),
                where("chatId", "==", existingChatId)
              );
              const snap = await getDocs(fallbackQ);
              const msgs = [];
              snap.forEach((d) => msgs.push({ id: d.id, ...d.data() }));
              msgs.sort((a, b) => {
                const ta = a.timestamp?.toMillis?.() || 0;
                const tb = b.timestamp?.toMillis?.() || 0;
                return ta - tb;
              });
              setMessages(msgs);
            } catch (e) {
              console.error("Fallback load failed:", e);
            }
            setLoading(false);
          };
          fallbackLoad();
        }
      );
    } catch (error) {
      console.error("Error initializing chat:", error);
      setLoading(false);
    }
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        sendAudioMessage();
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      showToast("No se pudo acceder al micrófono. Verificá los permisos.", "error");
    }
  }, [showToast]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  }, [isRecording]);

  const sendAudioMessage = async () => {
    const currentChatId = chatIdRef.current;
    if (audioChunksRef.current.length === 0 || !currentChatId) return;

    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      // SEC: Validar tamaño antes de subir (10MB max)
      if (audioBlob.size > 10 * 1024 * 1024) {
        console.warn("Audio demasiado grande:", audioBlob.size);
        return;
      }
      const timestamp = Date.now();
      const audioRef = ref(storage, `chat_audios/${currentChatId}/${timestamp}.webm`);
      await uploadBytes(audioRef, audioBlob);
      const audioUrl = await getDownloadURL(audioRef);

      await addDoc(collection(db, "employee_chat_messages"), {
        chatId: currentChatId,
        senderId: String(userid),
        senderName: userName || "Empleado",
        message: "",
        timestamp: Timestamp.now(),
        read: false,
        type: "audio",
        audioUrl,
        audioTranscription: "Transcribiendo...",
        audioDuration: recordingDuration,
      });

      await updateDoc(doc(db, "employee_chats", currentChatId), {
        lastMessage: "🎤 Audio",
        lastMessageAt: Timestamp.now(),
        unreadCountAdmin: increment(1),
      });
    } catch (error) {
      console.error("Error sending audio:", error);
    } finally {
      setRecordingDuration(0);
    }
  };

  const playAudio = useCallback((audioUrl, msgId) => {
    if (playingAudioId === msgId) {
      audioPlayerRef.current?.pause();
      setPlayingAudioId(null);
      return;
    }

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
    }

    const audio = new Audio(audioUrl);
    audioPlayerRef.current = audio;
    setPlayingAudioId(msgId);

    audio.onended = () => setPlayingAudioId(null);
    audio.play();
  }, [playingAudioId]);

  // Optimistic: add message to UI immediately, deduplicates when onSnapshot fires
  const handleOptimisticMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const formatTimestamp = useCallback((ts) => {
    if (!ts) return "";
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
  }, []);

  const formatDuration = useCallback((seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  }, []);

  const userIdStr = useMemo(() => String(userid), [userid]);

  if (!selectedBuilding) {
    return (
      <div style={styles.emptyContainer}>
        <LuTriangleAlert size={48} color="#f59e0b" />
        <h2 style={styles.emptyTitle}>Sin edificio asignado</h2>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerIcon}>
          <LuMessageCircle size={20} color="#0080ff" />
        </div>
        <div>
          <h1 style={styles.headerTitle}>Chat con Administración</h1>
          <p style={styles.headerSubtitle}>{selectedBuilding.name}</p>
        </div>
      </div>

      {/* Messages Area */}
      <div style={styles.messagesContainer}>
        {loading ? (
          <div style={styles.loadingCenter}>
            <LuLoader size={24} color="#0080ff" style={{ animation: "spin 1s linear infinite" }} />
            <span style={{ fontSize: 14, color: "#6b7280", marginTop: 8 }}>Cargando mensajes...</span>
          </div>
        ) : messages.length === 0 ? (
          <div style={styles.emptyMessages}>
            <LuMessageCircle size={40} color="#d1d5db" />
            <p style={{ color: "#6b7280", fontSize: 14, marginTop: 12 }}>
              No hay mensajes aún. Enviá el primero.
            </p>
          </div>
        ) : (
          <MessageList
            messages={messages}
            userIdStr={userIdStr}
            playingAudioId={playingAudioId}
            playAudio={playAudio}
            formatDuration={formatDuration}
            formatTimestamp={formatTimestamp}
            messagesEndRef={messagesEndRef}
          />
        )}
      </div>

      {/* Input Bar — isolated, typing doesn't re-render messages */}
      {isRecording ? (
        <div style={styles.inputBar}>
          <div style={styles.recordingBar}>
            <LuSquare size={12} color="#ef4444" style={{ animation: "pulse 1s infinite" }} />
            <span style={styles.recordingText}>Grabando {formatDuration(recordingDuration)}</span>
            <button style={styles.stopRecordBtn} onClick={stopRecording}>
              <LuSquare size={14} />
              Enviar audio
            </button>
          </div>
        </div>
      ) : (
        <ChatInputBar
          chatId={chatId}
          userid={userid}
          userName={userName}
          onStartRecording={startRecording}
          onOptimisticMessage={handleOptimisticMessage}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 80px)",
    maxWidth: 800,
    margin: "0 auto",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    gap: 12,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 400,
    color: "#111827",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "20px 24px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: "#f0f7ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  headerSubtitle: {
    fontSize: 13,
    color: "#6b7280",
    margin: "2px 0 0",
  },
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 24px",
    backgroundColor: "#f5f7fa",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  loadingCenter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
  emptyMessages: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
  messageBubbleRow: {
    display: "flex",
    width: "100%",
  },
  messageBubble: {
    maxWidth: "70%",
    padding: "10px 14px",
    borderRadius: 16,
    position: "relative",
  },
  senderName: {
    fontSize: 11,
    fontWeight: 400,
    color: "#6b7280",
    display: "block",
    marginBottom: 4,
  },
  messageTime: {
    fontSize: 10,
    display: "block",
    textAlign: "right",
    marginTop: 4,
  },
  audioRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  audioPlayBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
  },
  audioWave: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    flex: 1,
  },
  inputBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 24px",
    backgroundColor: "#ffffff",
    borderTop: "1px solid #e5e7eb",
  },
  textInput: {
    flex: 1,
    padding: "10px 16px",
    border: "1px solid #e5e7eb",
    borderRadius: 24,
    fontSize: 14,
    outline: "none",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  sendBtn: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "#0080ff",
    color: "#ffffff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  audioBtn: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "#f3f4f6",
    color: "#6b7280",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  recordingBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
    padding: "8px 16px",
    backgroundColor: "#fef2f2",
    borderRadius: 24,
  },
  recordingText: {
    fontSize: 14,
    color: "#ef4444",
    fontWeight: 400,
    flex: 1,
  },
  stopRecordBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    backgroundColor: "#ef4444",
    color: "#ffffff",
    border: "none",
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
};

export default ChatEmployee;
